import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { PhoneOtpModal } from './PhoneOtpModal';
import { useAuthStore } from '../../store/authStore';
import { api, API_ENDPOINTS } from '../../lib/api';
import { authService } from '../../services/authService';
import { User } from '../../types/auth';
import { captureError } from '../../lib/errorHandler';
import { Zap, Loader2 } from 'lucide-react';

/**
 * PhoneCollectionScreen
 *
 * Shown to new social sign-up users (isNew === true && user.phone === null).
 * Phone ownership is proved with Firebase Phone Auth before PATCH.
 * Collecting the phone enables CSMS registration (charging session capability).
 *
 * Skip logic: If user already has a phone (email-linked accounts or returning
 * social users who previously submitted), redirect to /app/map immediately.
 *
 * Route: /app/phone-collection (inside ProtectedLayout — JWT is available)
 */
export function PhoneCollectionScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  // Callers that hit a phone-required wall (e.g. wallet recharge) pass where to
  // resume; plain sign-up has no state and falls back to the map.
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? '/app/map';
  const { user, setUser } = useAuthStore();
  const [phone, setPhone] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPhoneOtpModal, setShowPhoneOtpModal] = useState(false);
  const [pendingPhone, setPendingPhone] = useState('');
  // Kept after a successful OTP so a failed PATCH can be retried without a new SMS —
  // the verification code is single-use, so re-opening the modal costs the user another one.
  const [phoneIdToken, setPhoneIdToken] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  // Skip logic: user already has a phone — no need for this screen
  useEffect(() => {
    if (user?.phone) {
      navigate(returnTo, { replace: true });
    }
  }, [user, navigate, returnTo]);

  const digits = phone.replace(/\D/g, '');

  const validatePhone = (value: string): boolean => {
    if (!value.trim()) {
      setValidationError('Phone number is required');
      return false;
    }
    const phoneDigits = value.replace(/\D/g, '');
    if (!/^[6-9]\d{9}$/.test(phoneDigits)) {
      setValidationError('Please enter a valid 10-digit mobile number starting with 6-9');
      return false;
    }
    setValidationError(null);
    return true;
  };

  const submitPhone = async (phoneToSave: string, phoneVerificationToken: string) => {
    setIsSubmitting(true);
    setServerError(null);
    try {
      await api.patch(API_ENDPOINTS.AUTH.COLLECT_PHONE, {
        phone: phoneToSave,
        phoneVerificationToken,
      });

      if (user) {
        setUser({ ...user, phone: phoneToSave } as User);
      }

      navigate(returnTo, { replace: true });
    } catch (err: any) {
      captureError(err, 'PhoneCollection.collectPhone', { phone: phoneToSave });
      const status = err?.response?.status;
      // A 4xx means the server rejected this token — an expired one included — so
      // retrying the same bytes can't succeed. Drop it and fall back to a fresh OTP.
      // 429 and 5xx never reached the check, so the token is still worth reusing.
      if (status >= 400 && status < 500 && status !== 429) {
        setPhoneIdToken(null);
      }
      const msg = err?.response?.data?.message ?? 'Failed to save phone number. Please try again.';
      setServerError(Array.isArray(msg) ? msg.join('. ') : msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);

    if (!validatePhone(digits)) return;

    // Already verified this number — a previous PATCH failed, so retry it directly.
    if (phoneIdToken && pendingPhone === digits) {
      await submitPhone(digits, phoneIdToken);
      return;
    }

    setIsChecking(true);
    try {
      await authService.checkPhone(digits);
    } catch (err: any) {
      captureError(err, 'PhoneCollection.checkPhone', { phone: digits });
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      if (status === 409) {
        setValidationError(serverMsg || 'An account with this phone number already exists');
      } else if (status === 429) {
        setValidationError('Too many attempts. Please try again in a minute.');
      } else {
        setValidationError("Couldn't verify this phone number. Please check your connection and try again.");
      }
      return;
    } finally {
      setIsChecking(false);
    }

    setPendingPhone(digits);
    setShowPhoneOtpModal(true);
  };

  // Close the modal before the PATCH: PhoneOtpModal latches `completedRef` once the
  // code is confirmed, so leaving it open on failure makes Verify a silent no-op.
  const handlePhoneVerified = async (phoneVerificationToken: string) => {
    setPhoneIdToken(phoneVerificationToken);
    setShowPhoneOtpModal(false);
    await submitPhone(pendingPhone, phoneVerificationToken);
  };

  const displayError = validationError || serverError;
  const busy = isChecking || isSubmitting;
  const canRetrySave = !!phoneIdToken && pendingPhone === digits;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        {/* Logo */}
        <div className="flex items-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <span className="text-2xl font-bold text-foreground">EVee Buddy</span>
        </div>

        {/* Heading */}
        <h1 className="text-2xl font-semibold text-foreground mb-2">One last step</h1>
        <p className="text-muted-foreground text-center mb-8">
          We need your phone number to enable charging sessions. We'll send an OTP to verify it.
        </p>

        {/* Phone Form */}
        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <div className="flex">
              {/* Country code prefix — non-editable, matches RegisterScreen pattern */}
              <div className="flex items-center px-3 border border-r-0 border-input rounded-l-md bg-muted text-muted-foreground text-sm">
                +91
              </div>
              <Input
                id="phone"
                type="tel"
                placeholder="Enter 10-digit mobile number"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
                  setValidationError(null);
                  setServerError(null);
                }}
                autoComplete="tel-national"
                inputMode="numeric"
                maxLength={10}
                disabled={busy || showPhoneOtpModal}
                className="rounded-l-none"
              />
            </div>
          </div>

          {/* Error Message */}
          {displayError && (
            <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
              {displayError}
            </div>
          )}

          {/* Submit Button */}
          <Button type="submit" className="w-full" size="lg" disabled={busy || showPhoneOtpModal}>
            {isChecking ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Checking...
              </>
            ) : isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Saving...
              </>
            ) : canRetrySave ? (
              'Save number'
            ) : (
              'Send OTP'
            )}
          </Button>
        </form>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 text-center">
        <p className="text-xs text-muted-foreground">
          Your phone number is used only for charging session management
        </p>
      </div>

      <PhoneOtpModal
        open={showPhoneOtpModal}
        onOpenChange={setShowPhoneOtpModal}
        phone={pendingPhone}
        onVerified={handlePhoneVerified}
      />
    </div>
  );
}
